import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./auth/AuthProvider.jsx";
import { ToastProvider } from "./ui/Toasts.jsx";
import LoginGate from "./auth/LoginGate.jsx";
import AppShell from "./AppShell.jsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Rental status changes while the app is open, so refetching on focus is
      // wanted. Retrying is not: an RLS denial returns empty rather than an
      // error, and a genuine error will not fix itself by asking again.
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function Centered({ children }) {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-top">
          <div className="wordmark">
            HAL<span>CO</span> OPS
          </div>
        </div>
        <div className="login-body">{children}</div>
      </div>
    </div>
  );
}

function Gate() {
  const { user, profile, profileMissing, loading, error, signOut } = useAuth();

  if (loading) {
    return (
      <Centered>
        <div style={{ textAlign: "center", color: "var(--muted)" }}>Loading…</div>
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <div className="notice red" style={{ margin: 0 }}>
          <b>Could not load your profile.</b>
          <div style={{ marginTop: 6 }}>{error}</div>
        </div>
        <button className="btn ghost" onClick={signOut} style={{ justifyContent: "center" }}>
          Sign out
        </button>
      </Centered>
    );
  }

  if (!user) return <LoginGate />;

  // Signed in, but no profiles row. Worth its own screen: RLS would otherwise
  // let this user through to an app where every table silently returns nothing,
  // which looks like the app is broken rather than like a missing permission.
  if (profileMissing) {
    return (
      <Centered>
        <div className="notice red" style={{ margin: 0 }}>
          <b>Your account has no access yet.</b>
          <div style={{ marginTop: 6 }}>
            Signing in worked, but <code>{user.email}</code> has not been granted a
            role, so every screen would be empty.
          </div>
          <div style={{ marginTop: 8 }}>
            An admin needs to run: <code>npm run grant -- {user.email} "Your Name" staff</code>
          </div>
        </div>
        <button className="btn ghost" onClick={signOut} style={{ justifyContent: "center" }}>
          Sign out
        </button>
      </Centered>
    );
  }

  if (!profile) {
    return (
      <Centered>
        <div style={{ textAlign: "center", color: "var(--muted)" }}>Loading profile…</div>
      </Centered>
    );
  }

  return <AppShell />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Gate />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
